import type { PayrollCalculatorEditableRow } from '@/lib/api/accounting';

/** متغیرهای قابل استفاده در فرمول — نام انگلیسی و فارسی */
export const PAYROLL_FORMULA_VARIABLES: Array<{ key: string; aliases: string[]; label: string }> = [
  { key: 'base_salary', aliases: ['حقوق_پایه', 'حقوق_ماهانه', 'حقوق'], label: 'حقوق پایه / ماهانه' },
  { key: 'daily_salary', aliases: ['دستمزد_روز', 'دستمزد', 'حقوق_روز'], label: 'دستمزد روزانه' },
  { key: 'worked_salary', aliases: ['حقوق_کارکرد', 'کارکرد'], label: 'حقوق کارکرد' },
  { key: 'absence_deduction', aliases: ['کسر_غیبت', 'کسر'], label: 'کسر غیبت' },
  { key: 'overtime_hours', aliases: ['ساعات_اضافه', 'اضافه_کار', 'اضافه'], label: 'ساعات اضافه‌کار' },
  { key: 'overtime_hourly_rate', aliases: ['نرخ_اضافه'], label: 'نرخ ساعتی اضافه‌کار' },
  { key: 'manual_advance_deduction', aliases: ['مساعده', 'کسور'], label: 'مساعده / کسور دستی' },
  { key: 'manual_incentive', aliases: ['تشویقی'], label: 'تشویقی دستی' },
  { key: 'present_days', aliases: ['روز_حاضر', 'تعداد_حضور', 'حضور', 'تعداد'], label: 'تعداد روز حاضر' },
  { key: 'absent_days', aliases: ['روز_غایب', 'غیبت'], label: 'روز غایب' },
  { key: 'half_days', aliases: ['نیم_روز'], label: 'نیم‌روز' },
  { key: 'worked_days', aliases: ['روز_کارکرد'], label: 'روز کارکرد' },
  { key: 'working_days_per_month', aliases: ['روزهای_ماه', 'روز_کاری_ماه'], label: 'روزهای کاری ماه' },
  { key: 'total_days_in_month', aliases: ['روزهای_ماه_تقویم'], label: 'تعداد روز ماه' },
  { key: 'gross_salary', aliases: ['ناخالص'], label: 'حقوق ناخالص' },
  { key: 'net_salary', aliases: ['خالص_سیستم'], label: 'خالص محاسبه‌شده سیستم' },
  { key: 'computed_net', aliases: ['خالص_فعلی'], label: 'خالص فعلی جدول' },
];

/** عبارت‌های فارسی با فاصله (قبل از جایگزینی متغیرها) */
const PAYROLL_FORMULA_PHRASES: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /تعداد\s*حضور/g, key: 'present_days' },
  { pattern: /روز\s*حاضر/g, key: 'present_days' },
  { pattern: /حقوق\s*پایه/g, key: 'base_salary' },
  { pattern: /دستمزد\s*روز/g, key: 'daily_salary' },
  { pattern: /حقوق\s*کارکرد/g, key: 'worked_salary' },
  { pattern: /کسر\s*غیبت/g, key: 'absence_deduction' },
  { pattern: /ساعات\s*اضافه/g, key: 'overtime_hours' },
  { pattern: /روزهای\s*ماه/g, key: 'working_days_per_month' },
];

export const PAYROLL_FORMULA_PRESETS: Array<{ id: string; label: string; formula: string }> = [
  {
    id: 'attendance_x_daily',
    label: 'تعداد حضور × دستمزد روز',
    formula: 'present_days * daily_salary',
  },
  {
    id: 'attendance_x_monthly_rate',
    label: 'تعداد حضور × (حقوق پایه ÷ روزهای ماه)',
    formula: 'present_days * (base_salary / working_days_per_month)',
  },
  {
    id: 'full_monthly',
    label: 'حقوق کامل ماهیانه',
    formula:
      'base_salary - absence_deduction + overtime_hours * overtime_hourly_rate - manual_advance_deduction + manual_incentive',
  },
  {
    id: 'base_x_present',
    label: 'حقوق پایه × (حضور ÷ روزهای ماه)',
    formula: 'base_salary * (present_days / working_days_per_month)',
  },
  {
    id: 'worked',
    label: 'بر اساس کارکرد + اضافه‌کار',
    formula:
      'worked_salary + overtime_hours * overtime_hourly_rate - manual_advance_deduction + manual_incentive - absence_deduction',
  },
  {
    id: 'base_only',
    label: 'فقط حقوق پایه',
    formula: 'base_salary',
  },
];

function buildVariableValues(row: PayrollCalculatorEditableRow): Record<string, number> {
  return {
    base_salary: Number(row.base_salary || 0),
    daily_salary: Number(row.daily_salary || 0),
    worked_salary: Number(row.worked_salary || 0),
    absence_deduction: Number(row.absence_deduction || 0),
    overtime_hours: Number(row.overtime_hours || 0),
    overtime_hourly_rate: Number(row.overtime_hourly_rate || 0),
    manual_advance_deduction: Number(row.manual_advance_deduction || 0),
    manual_incentive: Number(row.manual_incentive || 0),
    present_days: Number(row.present_days || 0),
    absent_days: Number(row.absent_days || 0),
    half_days: Number(row.half_days || 0),
    worked_days: Number(row.worked_days || 0),
    working_days_per_month: Number(row.working_days_per_month || 30) || 30,
    total_days_in_month: Number(row.total_days_in_month || 30) || 30,
    gross_salary: Number(row.gross_salary || 0),
    net_salary: Number(row.net_salary || 0),
    computed_net: Number(row.computed_net || 0),
  };
}

/** نرمال‌سازی علامت‌ها (ضرب، تقسیم، ×) */
export function normalizeFormulaSyntax(formula: string): string {
  return formula
    .trim()
    .replace(/×/g, '*')
    .replace(/÷/g, '/')
    .replace(/ضرب/g, '*')
    .replace(/تقسیم/g, '/')
    .replace(/\s+/g, ' ');
}

function applyPhraseAliases(expr: string): string {
  let out = expr;
  for (const { pattern, key } of PAYROLL_FORMULA_PHRASES) {
    out = out.replace(pattern, key);
  }
  return out;
}

function substituteVariables(expression: string, values: Record<string, number>): string {
  let expr = applyPhraseAliases(normalizeFormulaSyntax(expression));

  const tokens: Array<{ name: string; key: string }> = [];
  for (const v of PAYROLL_FORMULA_VARIABLES) {
    tokens.push({ name: v.key, key: v.key });
    for (const alias of v.aliases) {
      tokens.push({ name: alias, key: v.key });
    }
  }
  tokens.sort((a, b) => b.name.length - a.name.length);

  for (const { name, key } of tokens) {
    const val = values[key] ?? 0;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    expr = expr.replace(new RegExp(`(^|[^\\w])${escaped}([^\\w]|$)`, 'g'), `$1${val}$2`);
    expr = expr.replace(new RegExp(`\\b${escaped}\\b`, 'g'), String(val));
  }

  return expr.replace(/\s+/g, '');
}

export function previewPayrollFormula(formula: string, row: PayrollCalculatorEditableRow): string {
  const substituted = substituteVariables(formula, buildVariableValues(row));
  return substituted;
}

export function evaluatePayrollFormula(formula: string, row: PayrollCalculatorEditableRow): number {
  const trimmed = formula.trim();
  if (!trimmed) {
    throw new Error('فرمول خالی است.');
  }
  const substituted = substituteVariables(trimmed, buildVariableValues(row));
  if (!/^[\d+\-*/().]+$/.test(substituted)) {
    throw new Error(`فرمول نامعتبر: «${substituted}». از متغیرها و + - * / ( ) استفاده کنید.`);
  }
  try {
    const result = Function(`"use strict"; return (${substituted});`)() as number;
    if (typeof result !== 'number' || !Number.isFinite(result)) {
      throw new Error('نتیجه فرمول عدد معتبر نیست.');
    }
    return Math.round(result);
  } catch {
    throw new Error('خطا در محاسبه فرمول.');
  }
}

export type PayrollNetMode = 'auto' | 'manual' | 'formula';

export function resolveRowNet(row: PayrollCalculatorEditableRow): number {
  if (row.net_mode === 'manual') {
    return Math.round(Number(row.computed_net || 0));
  }
  if (row.net_mode === 'formula' && row.net_formula?.trim()) {
    return evaluatePayrollFormula(row.net_formula, row);
  }
  const overtimePay = Math.round(Number(row.overtime_hours || 0) * Number(row.overtime_hourly_rate || 0));
  const gross = Number(row.worked_salary || 0) + overtimePay + Number(row.manual_incentive || 0);
  return Math.round(gross - Number(row.absence_deduction || 0) - Number(row.manual_advance_deduction || 0));
}
